"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Building, CircleCheck as CheckCircle2, ChevronRight, House as Home, Layers, MapPin, ShieldCheck, Umbrella, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const coverageAreas = ["Town-centre commercial properties", "Office developments & retail units", "Residential & suburban neighbourhoods", "Refurbishment & mixed-use buildings", "Specialist roof & chimney projects"];
const residentialScopes = ["Roof repairs & structural renewals", "Single & multi-storey home extensions", "Chimney stack maintenance & rebuilds", "External wall rendering & repointing", "Loft conversion scaffolding & gable access"];
const commercialScopes = ["Main contractors & multi-trade site programs", "Commercial refurbishment & phased alterations", "Occupied office & public-facing properties", "Pedestrian protection & gantry systems", "Strict RAMS, TG20 compliance & weekly inspections"];
const tradeAccessItems = [{
  title: "Roofing Scaffolding",
  description: "Stable perimeter platforms, handrails, and loading bays tailored for roofers.",
  to: "/RoofingScaffoldingPage"
}, {
  title: "Temporary Roofs",
  description: "Weather-tight corrugated or shrink-wrap coverings ensuring dry project continuity.",
  to: "/TemporaryRoofsPage"
}, {
  title: "Edge Protection",
  description: "NASC-compliant handrails, toe boards, and debris netting for fall prevention.",
  to: "/EdgeProtectionPage"
}];
export default function ReadingCapabilities() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUpVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="readingscaffoldingcapabilities" data-nav="light" className="relative bg-background py-20 lg:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeUpVariant} className="mx-auto max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-muted/60 px-3.5 py-1 text-xs font-medium text-foreground">
            <MapPin className="h-3.5 w-3.5 text-primary" />
            <span>Reading & Berkshire Project Coverage</span>
          </div>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Scaffolding capabilities across Reading
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            From tight urban town-centre sites to suburban residential streets,
            we deliver planned access solutions engineered for safety, compliance,
            and continuous trade progress.
          </p>
        </motion.div>

        {/* Coverage Highlights Strip */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeUpVariant} className="mt-10 rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Sector Coverage in Reading
          </div>
          <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-5">
            {coverageAreas.map((item, index) => <div key={item} data-index={index} className="flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-2 text-xs font-medium text-foreground">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                <span>{item}</span>
              </div>)}
          </div>
        </motion.div>

        {/* Two Main Subsections: Residential & Commercial */}
        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Residential Scaffolding Card */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeUpVariant} className="flex">
            <Card className="flex w-full flex-col justify-between border-border bg-card shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
              <CardHeader className="pb-4">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Home className="h-5 w-5" />
                  </div>
                  <Badge variant="outline" className="border-border">
                    Domestic & Property Works
                  </Badge>
                </div>
                <CardTitle className="font-default text-2xl font-bold text-foreground">
                  Residential scaffolding in Reading
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  Careful, reliable access for homeowners, builders, and specialist
                  trades working on domestic properties throughout Reading.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6 pt-2">
                <div className="space-y-2.5">
                  <div className="text-xs font-semibold tracking-wide text-foreground">
                    Common domestic applications:
                  </div>
                  <ul className="space-y-2">
                    {residentialScopes.map((scope, index) => <li key={scope} data-index={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span>{scope}</span>
                      </li>)}
                  </ul>
                </div>

                <Separator />

                <div className="space-y-2.5 rounded-lg bg-muted/40 p-4">
                  <div className="text-xs font-semibold text-foreground">
                    Dedicated Service Pages:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button asChild variant="outline" size="sm" className="h-8 text-xs">
                      <Link to="/ResidentialScaffoldingPage">
                        <span>Residential Scaffolding</span>
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                    </Button>
                    <Button asChild variant="outline" size="sm" className="h-8 text-xs">
                      <Link to="/ChimneyScaffoldingPage">
                        <span>Chimney Scaffolding</span>
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>

              <CardFooter className="pt-2">
                <Button asChild className="w-full">
                  <Link to="/ContactRequestQuotePage">
                    <span>Request residential quote</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Commercial Scaffolding Card */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeUpVariant} className="flex">
            <Card className="flex w-full flex-col justify-between border-border bg-card shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
              <CardHeader className="pb-4">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary text-secondary-foreground">
                    <Building className="h-5 w-5" />
                  </div>
                  <Badge variant="secondary">
                    Main Contractors & Commercial
                  </Badge>
                </div>
                <CardTitle className="font-default text-2xl font-bold text-foreground">
                  Commercial scaffolding in Reading
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  Engineered access programs for main contractors, town-centre
                  refurbishments, and occupied commercial properties.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6 pt-2">
                <div className="space-y-2.5">
                  <div className="text-xs font-semibold tracking-wide text-foreground">
                    Commercial & contractor capabilities:
                  </div>
                  <ul className="space-y-2">
                    {commercialScopes.map((scope, index) => <li key={scope} data-index={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span>{scope}</span>
                      </li>)}
                  </ul>
                </div>

                <Separator />

                <div className="space-y-2.5 rounded-lg bg-muted/40 p-4">
                  <div className="text-xs font-semibold text-foreground">
                    Dedicated Service Pages:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button asChild variant="outline" size="sm" className="h-8 text-xs">
                      <Link to="/CommercialScaffoldingPage">
                        <span>Commercial Scaffolding</span>
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                    </Button>
                    <Button asChild variant="outline" size="sm" className="h-8 text-xs">
                      <Link to="/MainContractorsDevelopersPage">
                        <span>Main Contractors & Developers</span>
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>

              <CardFooter className="pt-2">
                <Button asChild variant="secondary" className="w-full">
                  <Link to="/ContactRequestQuotePage">
                    <span>Discuss commercial tender</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </div>

        {/* Roofing & Trade Access Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeUpVariant} className="mt-12 rounded-2xl border border-border bg-muted/30 p-6 sm:p-8 lg:p-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 rounded-md bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                <Wrench className="h-3.5 w-3.5" />
                <span>Specialist Support</span>
              </div>
              <h3 className="mt-3 font-default text-2xl font-bold text-foreground sm:text-3xl">
                Roofing & trade access
              </h3>
              <p className="mt-2 text-sm text-muted-foreground sm:text-base">
                Purpose-built platforms designed specifically for roofing contractors,
                solar installers, and exterior renovation teams requiring safe working
                heights and dependable weather protection.
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-3">
              <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2 text-xs font-medium text-muted-foreground shadow-2xs">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <span>NASC & CHAS</span>
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {tradeAccessItems.map((item, index) => <div key={item.title} data-index={index} className="group flex flex-col justify-between rounded-xl border border-border bg-card p-5 shadow-2xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-sm">
                <div>
                  <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-md bg-muted text-foreground">
                    {index === 0 && <Layers className="h-4 w-4 text-primary" />}
                    {index === 1 && <Umbrella className="h-4 w-4 text-primary" />}
                    {index === 2 && <ShieldCheck className="h-4 w-4 text-primary" />}
                  </div>
                  <h4 className="font-default text-base font-semibold text-foreground">
                    {item.title}
                  </h4>
                  <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>
                <div className="mt-5 pt-3">
                  <Button asChild variant="ghost" size="sm" className="h-8 w-full justify-between px-2 text-xs font-medium text-foreground hover:text-primary">
                    <Link to={item.to}>
                      <span>Explore {item.title}</span>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </Button>
                </div>
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}